/**
 * PlmFinalTab — the read-only "Final Confirmation" tab (prototype v8,
 * `#pane-final`).
 *
 * It adds NO data of its own: it restates, on one screen, everything the
 * analyst entered on the Stereoscopic and PLM Exam tabs, plus the verdict the
 * report will carry. That is the point — before submitting, the analyst reads
 * back the whole layer in one place instead of paging between two tabs.
 *
 *     ┌───────────────────────────────┬──────────────────┐
 *     │ Material · Friability · …     │ Asbestos         │
 *     │ (stereo summary grid)         │ Recognition      │
 *     ├───────────────────────────────┤   YES / NO       │
 *     │ component table with optics   │  + the types     │
 *     └───────────────────────────────┴──────────────────┘
 *
 * ACM threshold: a layer is "asbestos-containing" when any Asbestos Fibers
 * component reads MORE than {@link ACM_THRESHOLD_PCT}% — EPA 600/R-93/116.
 * Percentages are free text in legacy ("<1", "Trace", "2.5"), so
 * {@link parsePercent} returns null for anything that is not a plain number
 * and a trace reading therefore never trips the ACM verdict on its own.
 */
import { useMemo } from 'react';
import type { PlmExamInput, PlmObservationInput } from './plm.api';
import type { PointCountType } from './plmPointCount';
import {
  COLOURS, COLOUR_TEXT, FRIABILITY, HOMOGENEITY, HOMOGENEITY_TEXT,
  LEGACY_TREATMENTS, TEXTURES, TEXTURE_TEXT, TREATMENTS, summarizeGroup,
} from './plmStereoOptions';
import './plm.css';

/** Above this percentage the layer is reported as asbestos-containing. */
export const ACM_THRESHOLD_PCT = 1;

/** The FiberTypes value that marks a component as asbestos. Load-bearing —
 *  the same string keys the exam grid's component-name list. */
const ASBESTOS = 'Asbestos Fibers';

/**
 * Reads a legacy free-text percentage. Returns the number for a plain numeric
 * reading ("2", "2.5", " 2.5 %"), and null for "<1", "Trace", "ND" or blank —
 * those are real readings but not quantities, so they must not be compared
 * against the ACM threshold as if they were.
 */
export function parsePercent(text: string | null | undefined): number | null {
  if (!text) return null;
  const m = /^\s*([0-9]+(?:\.[0-9]+)?)\s*%?\s*$/.exec(text);
  return m ? Number(m[1]) : null;
}

type Props = {
  /** The layer's stereoscopic observation. */
  obs: PlmObservationInput;
  /** The layer's exam rows, in grid order. */
  exams: PlmExamInput[];
  /** Resolved component (FiberTypes) name per exam row, by index. */
  fiberTypeNames: (string | null)[];
  /** The layer's Material Description (an older layer: its material names). */
  materialNames: string | null;
  /** Point-count type in force, or null for a visual estimate. */
  pointCountType: PointCountType | null;
  /** Slide/point counts per type, for the "400 points" caption. */
  pointCountLabel?: string;
};

const FRIABILITY_LABEL = new Map(FRIABILITY);

/** Joins the ticked optics flags of an EXAM row into "A, B, C" (empty → em
 *  dash). The stereo groups above use the shared summarizeGroup instead. */
const joinFlags = (pairs: [boolean, string][], other?: string | null) => {
  const on = pairs.filter(([v]) => v).map(([, label]) => label);
  if (other?.trim()) on.push(other.trim());
  return on.length ? on.join(', ') : '—';
};

export function PlmFinalTab({
  obs, exams, fiberTypeNames, materialNames, pointCountType, pointCountLabel,
}: Props) {
  const asbestos = useMemo(
    () => exams
      .map((e, i) => ({ row: e, type: fiberTypeNames[i] }))
      .filter(x => x.type === ASBESTOS),
    [exams, fiberTypeNames],
  );

  // "Asbestos-containing" needs a QUANTITY over the threshold. A layer can
  // legitimately list Chrysotile at "<1" or "Trace": asbestos was recognised
  // (YES) but the material is not ACM.
  const isAcm = asbestos.some(x => (parsePercent(x.row.result) ?? 0) > ACM_THRESHOLD_PCT);

  // Texture is no longer entered (owner feedback 2026-10-07); only a layer
  // saved before that still carries one, and only such a layer shows the row.
  const texture = summarizeGroup(obs, TEXTURES, TEXTURE_TEXT);

  const summary: [string, string][] = [
    ['Material Description', materialNames?.trim() || '—'],
    ['Friability', obs.friability != null ? (FRIABILITY_LABEL.get(obs.friability) ?? '—') : '—'],
    // Every group reads back from the SAME option list the Stereoscopic tab
    // renders (plmStereoOptions.ts), in its order and with its labels. The
    // Final tab used to keep a private copy that missed Fibrous / Non Fibrous,
    // so a Fibrous-only layer showed "—" here (owner report 2026-09-25).
    ...(texture !== '—' ? [['Texture', texture] as [string, string]] : []),
    ['Treatment', summarizeGroup(obs, [...TREATMENTS, ...LEGACY_TREATMENTS])],
    ['Homogeneity', summarizeGroup(obs, HOMOGENEITY, HOMOGENEITY_TEXT)],
    // The prototype's right column carries the stereo PERCENTAGE here, not
    // "Fibers present" — the % is what the report prints and what the ACM
    // verdict beside it is read against.
    ['Stereo asbestos %', obs.noAsbestosDetected
      ? 'No Asbestos Detected'
      : (obs.fiberObservation?.trim() || '—')],
    ['Color', summarizeGroup(obs, COLOURS, COLOUR_TEXT)],
    ['Point count', pointCountType
      ? (pointCountLabel ?? `${pointCountType} points`)
      : 'Visual estimate'],
  ];

  return (
    <div className="plm-final">
      <div className="plm-final__left">
        {/* Label : boxed value, two columns — the prototype's `.fgrid2`. The
            value sits in a read-only field box so the summary reads like the
            form it is summarising, rather than like prose. */}
        <dl className="plm-final__grid">
          {summary.map(([label, value]) => (
            <div className="plm-final__cell" key={label}>
              <dt>{label}</dt>
              <dd><span className="plm-final__box">{value}</span></dd>
            </div>
          ))}
        </dl>

        <div className="plm-final__tablewrap">
          <table className="plm-final__table">
            <thead>
              <tr>
                {/* Prototype v8 labels this column "Fiber Type" here (the exam
                    grid keeps "Component") — matched exactly, 2026-09-25. */}
                <th>Fiber Type</th><th>Fiber Name</th><th className="num">Quantity</th>
                <th>Pleochroism</th><th>Morphology</th><th>Extinction</th>
                <th>Elongation</th><th>Birefringence</th><th>Refractive Index</th>
              </tr>
            </thead>
            <tbody>
              {exams.length === 0 ? (
                <tr><td colSpan={9} className="plm-final__empty">No components entered.</td></tr>
              ) : exams.map((e, i) => (
                <tr key={e.examOid ?? `new-${i}`}>
                  <td>{fiberTypeNames[i] ?? '—'}</td>
                  <td>{e.name || '—'}</td>
                  <td className="num">{e.result || '—'}</td>
                  <td>{joinFlags([[e.pleochroismYes, 'Yes'], [e.pleochroismNo, 'No']],
                    e.pleochroismColor)}</td>
                  <td>{joinFlags([
                    [e.morphologyBundles, 'Bundles'], [e.morphologyCurved, 'Curved'],
                    [e.morphologyRibbon, 'Ribbon'], [e.morphologySplayed, 'Splayed'],
                    [e.morphologyStraight, 'Straight'], [e.morphologyWavey, 'Wavey'],
                    [e.morphologyUniform, 'Uniform'],
                  ], e.morphologyText)}</td>
                  <td>{joinFlags([
                    [e.extinctionOblique, 'Oblique'], [e.extinctionParallel, 'Parallel'],
                    [e.extinctionUndulose, 'Undulose'], [e.extinctionIncomplete, 'Incomplete'],
                    [e.extinctionNone, 'None'],
                  ])}</td>
                  <td>{joinFlags([
                    [e.elongationPositive, 'Positive'], [e.elongationNegative, 'Negative'],
                    [e.elongationBoth, 'Both'],
                  ])}</td>
                  <td>{joinFlags([
                    [e.typeLow, 'Low'], [e.typeMedium, 'Medium'],
                    [e.typeHigh, 'High'], [e.typeIsotropic, 'Isotropic'],
                  ])}</td>
                  <td>{e.gamma || e.alpha
                    ? `γ ${e.gamma || '—'} / α ${e.alpha || '—'}`
                    : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Positive Stop (G5 parity 2026-10-15): the layer is reported as
          "Positive Stop" — nothing else was entered on it. */}
      {obs.positiveStop ? (
      <aside className="plm-asbrec plm-asbrec--yes" aria-label="Asbestos recognition">
        <div className="plm-asbrec__label">Reported as</div>
        <div className="plm-asbrec__value" data-testid="plm-final-ps">Positive Stop</div>
        <div className="plm-asbrec__detail">
          A positive result on this layer ends analysis of the remaining layers of the same homogeneous area.
        </div>
      </aside>
      ) : (
      <aside
        className={`plm-asbrec plm-asbrec--${asbestos.length ? 'yes' : 'no'}`}
        aria-label="Asbestos recognition"
      >
        <div className="plm-asbrec__label">Asbestos Recognition</div>
        <div className="plm-asbrec__value">{asbestos.length ? 'YES' : 'NO'}</div>
        <div className="plm-asbrec__detail">
          {obs.noAsbestosDetected && asbestos.length === 0 ? 'No Asbestos Detected' : null}
          {asbestos.length === 0 && !obs.noAsbestosDetected ? 'None Detected' : null}
          {asbestos.length > 0 && (
            <>
              {asbestos.map((x, i) => (
                <div key={x.row.examOid ?? `a-${i}`}>
                  {x.row.name || '—'} {x.row.result || '—'}
                </div>
              ))}
              <b className="plm-asbrec__verdict">
                {isAcm
                  ? `Asbestos-containing (> ${ACM_THRESHOLD_PCT}%)`
                  : `≤ ${ACM_THRESHOLD_PCT}% — not ACM`}
              </b>
            </>
          )}
        </div>
      </aside>
      )}
    </div>
  );
}
